"use client";

import { useEffect, useState, useCallback, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Cookie, X } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";

interface CookieSettings {
  cookieBannerEnabled: boolean;
  cookieBannerTitle: string;
  cookieBannerMessage: string;
  cookieBannerAcceptText: string;
  cookieBannerRejectText: string;
  cookieBannerPolicyText: string;
  cookieBannerPolicyUrl: string;
  cookieBannerPosition: string;
  cookieBannerStyle: string;
  cookieBannerScope: string;
  cookieBannerShowRejectButton: boolean;
  cookieBannerRevisitDays: number;
  cookieBannerVersion: number;
}

const STORAGE_KEY = "ns_cookie_consent_v1";

interface StoredConsent {
  decision: "accept" | "reject";
  timestamp: number;
  version: number;
}

function readConsent(): StoredConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredConsent;
  } catch {
    return null;
  }
}

function writeConsent(decision: "accept" | "reject", version: number) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        decision,
        timestamp: Date.now(),
        version,
      } satisfies StoredConsent)
    );
  } catch {
    /* ignore */
  }
}

function matchesScope(scope: string, pathname: string): boolean {
  // Banner nie ma sensu w panelu admina
  if (pathname.startsWith("/admin")) return false;
  switch (scope) {
    case "landing":
      return pathname === "/" || pathname.startsWith("/blog") || pathname.startsWith("/rekrutacja") || pathname.startsWith("/wkrotce") || pathname.startsWith("/matura-2027") || pathname.startsWith("/polityka");
    case "public-only":
      return !pathname.startsWith("/platforma");
    case "all":
    default:
      return true;
  }
}

export default function CookieBanner() {
  const pathname = usePathname() || "/";
  const [settings, setSettings] = useState<CookieSettings | null>(null);
  const [visible, setVisible] = useState(false);
  // Ustawienia otwarte ręcznie przez pływający przycisk (nadpisuje zapisaną zgodę i zasięg)
  const [forcedOpen, setForcedOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/settings/public")
      .then((res) => res.json())
      .then((s: CookieSettings) => {
        if (cancelled) return;
        setSettings(s);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Decyzja czy pokazać
  useEffect(() => {
    if (!settings) return;
    if (!settings.cookieBannerEnabled) {
      setVisible(false);
      return;
    }
    if (!matchesScope(settings.cookieBannerScope, pathname)) {
      setVisible(false);
      return;
    }

    const stored = readConsent();
    if (!stored) {
      setVisible(true);
      return;
    }
    // Wersja banera zmieniona → ponowna zgoda
    if ((stored.version || 0) !== (settings.cookieBannerVersion || 1)) {
      setVisible(true);
      return;
    }
    // Sprawdź czy minęło X dni od decyzji
    const revisitMs = Math.max(0, settings.cookieBannerRevisitDays || 0) * 24 * 60 * 60 * 1000;
    if (revisitMs > 0 && Date.now() - stored.timestamp >= revisitMs) {
      setVisible(true);
      return;
    }
    setVisible(false);
  }, [settings, pathname]);

  // Otwarcie ustawień prywatności z innego miejsca (np. ustawień konta).
  useEffect(() => {
    const handler = () => setForcedOpen(true);
    window.addEventListener("ns:open-cookie-settings", handler);
    return () => window.removeEventListener("ns:open-cookie-settings", handler);
  }, []);

  const handleAccept = useCallback(() => {
    if (!settings) return;
    writeConsent("accept", settings.cookieBannerVersion || 1);
    setVisible(false);
    setForcedOpen(false);
    try {
      window.dispatchEvent(new CustomEvent("cookie-consent", { detail: { decision: "accept" } }));
    } catch {}
  }, [settings]);

  const handleReject = useCallback(() => {
    if (!settings) return;
    writeConsent("reject", settings.cookieBannerVersion || 1);
    setVisible(false);
    setForcedOpen(false);
    try {
      window.dispatchEvent(new CustomEvent("cookie-consent", { detail: { decision: "reject" } }));
    } catch {}
  }, [settings]);

  // Zawsze renderujemy AnimatePresence ze stabilnym kształtem drzewa, żeby
  // nie powodować mount/unmount tego komponentu przy zmianie strony - to
  // psuje reconcilera Reacta w App Routerze ("removeChild parentNode null")
  // i wymusza podwójne kliknięcie w linki.
  const bannerOpen = visible || forcedOpen;
  const ready = !!settings && bannerOpen;

  const isBar = settings?.cookieBannerStyle === "bar";
  const isModal = settings?.cookieBannerStyle === "modal";
  const isCard = !isBar && !isModal;
  // unikamy nieużywanej zmiennej (ESLint)
  void isCard;

  // Pozycja kontenera (dla bar/card). Modal jest zawsze centrowany.
  let containerClass = "fixed z-[9999] px-4";
  if (!isModal && settings) {
    switch (settings.cookieBannerPosition) {
      case "top":
        containerClass += " top-4 left-0 right-0 flex justify-center";
        break;
      case "bottom-left":
        containerClass += " bottom-4 left-4";
        break;
      case "bottom-right":
        containerClass += " bottom-4 right-4";
        break;
      case "center":
        containerClass += " inset-0 flex items-center justify-center";
        break;
      case "bottom":
      default:
        containerClass += " bottom-4 left-0 right-0 flex justify-center";
    }
  }

  const policyLink = settings?.cookieBannerPolicyUrl?.trim() || "/polityka-cookies";

  let bannerNode: ReactNode;

  // ── Bar (pasek na całą szerokość) ────────────────────────────────
  if (isBar) {
    const barPos =
      settings!.cookieBannerPosition === "top"
        ? "top-0 left-0 right-0"
        : "bottom-0 left-0 right-0";
    bannerNode = (
      <AnimatePresence>
        {ready && (
        <motion.div
          key="cookie-bar"
          initial={{ y: settings!.cookieBannerPosition === "top" ? -80 : 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: settings!.cookieBannerPosition === "top" ? -80 : 80, opacity: 0 }}
          className={`fixed ${barPos} z-[9999] bg-slate-900/95 backdrop-blur-md text-white border-t border-slate-700 shadow-2xl`}
          role="dialog"
          aria-live="polite"
          aria-label="Zgoda na pliki cookies"
        >
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col md:flex-row items-start md:items-center gap-3">
            <Cookie className="w-5 h-5 text-amber-300 shrink-0 hidden md:block" />
            <div className="flex-1 text-sm leading-relaxed">
              <span className="font-semibold mr-2">{settings!.cookieBannerTitle}</span>
              <span className="text-slate-200">{settings!.cookieBannerMessage}</span>{" "}
              <Link href={policyLink} className="underline text-blue-300 hover:text-blue-200 whitespace-nowrap">
                {settings!.cookieBannerPolicyText}
              </Link>
            </div>
            <div className="flex items-center gap-2 shrink-0 self-stretch md:self-auto">
              {settings!.cookieBannerShowRejectButton && (
                <button
                  onClick={handleReject}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-slate-700 hover:bg-slate-600 transition"
                >
                  {settings!.cookieBannerRejectText}
                </button>
              )}
              <button
                onClick={handleAccept}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 transition"
              >
                {settings!.cookieBannerAcceptText}
              </button>
            </div>
          </div>
        </motion.div>
        )}
      </AnimatePresence>
    );
  } else if (isModal) {
    // ── Modal (centrowany z backdropem) ────────────────────────
    bannerNode = (
      <AnimatePresence>
        {ready && (
        <motion.div
          key="cookie-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Zgoda na pliki cookies"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 relative"
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                <Cookie className="w-6 h-6 text-amber-500" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">{settings!.cookieBannerTitle}</h2>
              </div>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">{settings!.cookieBannerMessage}</p>
            <Link href={policyLink} className="inline-block text-sm text-blue-600 hover:text-blue-700 underline mb-5">
              {settings!.cookieBannerPolicyText} →
            </Link>
            <div className="flex flex-col sm:flex-row gap-2">
              {settings!.cookieBannerShowRejectButton && (
                <button
                  onClick={handleReject}
                  className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition"
                >
                  {settings!.cookieBannerRejectText}
                </button>
              )}
              <button
                onClick={handleAccept}
                className="flex-1 px-4 py-2.5 text-sm font-semibold rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white transition shadow-sm"
              >
                {settings!.cookieBannerAcceptText}
              </button>
            </div>
          </motion.div>
        </motion.div>
        )}
      </AnimatePresence>
    );
  } else {
    // ── Card (domyślny) ───────────────────────────────────
    bannerNode = (
    <AnimatePresence>
      {ready && (
      <motion.div
        key="cookie-card"
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 30, opacity: 0 }}
        className={containerClass}
        role="dialog"
        aria-live="polite"
        aria-label="Zgoda na pliki cookies"
      >
        <div className="bg-white border border-slate-200/80 rounded-2xl shadow-2xl max-w-md w-full p-5 relative">
          <button
            onClick={handleReject}
            aria-label="Zamknij"
            className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 transition"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
              <Cookie className="w-5 h-5 text-amber-500" />
            </div>
            <h3 className="font-bold text-slate-900 text-sm">{settings!.cookieBannerTitle}</h3>
          </div>
          <p className="text-xs text-slate-600 leading-relaxed mb-3">{settings!.cookieBannerMessage}</p>
          <Link href={policyLink} className="block text-xs text-blue-600 hover:text-blue-700 underline mb-4">
            {settings!.cookieBannerPolicyText} →
          </Link>
          <div className="flex gap-2">
            {settings!.cookieBannerShowRejectButton && (
              <button
                onClick={handleReject}
                className="flex-1 px-3 py-2 text-xs font-medium rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition"
              >
                {settings!.cookieBannerRejectText}
              </button>
            )}
            <button
              onClick={handleAccept}
              className="flex-1 px-3 py-2 text-xs font-semibold rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white transition shadow-sm"
            >
              {settings!.cookieBannerAcceptText}
            </button>
          </div>
        </div>
      </motion.div>
      )}
    </AnimatePresence>
    );
  }

  // Pływający przycisk „Ustawienia prywatności i cookies" (lewy dolny róg).
  // Zawsze dostępny (poza panelem admina, platformą i trybem nauki lekcji), aby
  // użytkownik mógł w każdej chwili zmienić swoją zgodę. W panelu ucznia dostępne
  // w Ustawieniach konta. Ukrywany, gdy baner jest właśnie otwarty.
  const isLessonView = /^\/kurs\/[^/]+\/lekcja\//.test(pathname);
  const showSettingsButton =
    !!settings &&
    settings.cookieBannerEnabled &&
    !pathname.startsWith("/admin") &&
    !pathname.startsWith("/platforma") &&
    !isLessonView &&
    !bannerOpen;

  return (
    <>
      {bannerNode}
      <AnimatePresence>
        {showSettingsButton && (
          <motion.button
            key="cookie-settings-fab"
            type="button"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.94 }}
            onClick={() => setForcedOpen(true)}
            aria-label="Zmień ustawienia prywatności i cookies"
            title="Ustawienia prywatności i cookies"
            className="fixed bottom-4 left-4 z-[9998] w-11 h-11 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-lg flex items-center justify-center text-slate-500 dark:text-slate-300 hover:text-amber-500 hover:border-amber-300 dark:hover:border-amber-500/50 transition-colors"
            style={{ marginBottom: "env(safe-area-inset-bottom)" }}
          >
            <Cookie className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
}
